/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Ref } from 'preact/hooks';
import { useDatatip } from './useDatatip_FORK';
import { calculateOffset } from '../../hooks/PRIVATE_useDatatip';
import type { GanttViewportTaskLayoutObj } from '../utils/dataLayoutUtils';
import type { GanttProps, GanttRowData, GanttTaskData } from '../gantt.types';
import { getDefaultDatatipRenderer, getRenderContext } from '../utils/datatipUtils';
import { useTranslationBundle } from '../../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../../resources/nls/bundle';
import { useUser } from '../../hooks/UNSAFE_useUser';

type UseGanttTaskDatatipOptions<
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
> = {
  targetRef: Ref<HTMLElement>;
  datatip?: GanttProps<K2, D1, D2>['datatip'];
  rowData: GanttProps<K2, D1, D2>['rows'];
  dateFormatter: (date: string) => string;
  hoveredTaskObj?: GanttViewportTaskLayoutObj<K2>;
  focusedTaskObj?: GanttViewportTaskLayoutObj<K2>;
  currentTaskInteraction: 'hover' | 'focus' | 'none';
  dataBodyRegion: { x: number; y: number; w: number; h: number };
};

/**
 * Hook that handles task datatip.
 */
const useGanttTaskDatatip = <
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>({
  targetRef,
  datatip,
  rowData,
  dateFormatter,
  hoveredTaskObj,
  focusedTaskObj,
  currentTaskInteraction,
  dataBodyRegion
}: UseGanttTaskDatatipOptions<K1, K2, D1, D2>) => {
  const { direction } = useUser();
  const isRTL = direction === 'rtl';
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const config = {
    hover: {
      taskObj: hoveredTaskObj,
      anchor: 'pointer' as const
    },
    focus: {
      taskObj: focusedTaskObj,
      anchor: 'element' as const
    },
    none: {
      taskObj: undefined,
      anchor: 'pointer' as const
    }
  };

  const { anchor, taskObj: activeTaskObj } = config[currentTaskInteraction];

  const datatipRenderer =
    datatip ??
    getDefaultDatatipRenderer<K1, K2, D1, D2>({
      translations,
      dateFormatter,
      isRTL
    });

  const { content, borderColor } = activeTaskObj
    ? datatipRenderer(getRenderContext<K1, K2, D1, D2>(rowData, activeTaskObj))
    : { content: undefined, borderColor: undefined };

  const elementBounds =
    activeTaskObj && anchor === 'element'
      ? {
          x: activeTaskObj.x,
          y: activeTaskObj.y + dataBodyRegion.y,
          width: activeTaskObj.width,
          height: activeTaskObj.height
        }
      : undefined;

  return useDatatip({
    content,
    anchor,
    placement: 'top-start',
    offset: calculateOffset(isRTL, dataBodyRegion.w, elementBounds),
    borderColor,
    targetFocusRef: targetRef
  });
};

export { useGanttTaskDatatip };
